// IntelligenceDetail.jsx — the reader for a single brief.
//
// Layout (top to bottom):
//   1. Top nav row with a back button
//   2. Cover: THE BRIEFING title in serif, edition + date + bundle chip,
//      gold hairline divider
//   3. Listen CTA (collapses into sticky audio player once playing)
//   4. Table of contents — tappable anchors to each insight
//   5. Eight insights, each with section label, serif title, body paragraph,
//      Strategic Relevance callout, and source link pills
//
// Source links open via window.open, which on iOS launches Safari. A follow
// up can swap to @capacitor/browser for an in-app Safari view.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { BriefAudioPlayer } from '../components/BriefAudioPlayer.jsx';
import { bundleMeta } from '../lib/briefBundles.js';
import { briefStore, displayEditionFor } from '../lib/briefStore.js';
import { sfx } from '../lib/sfx.js';

function humanDate(iso) {
  if (!iso) return '';
  try {
    // Parse parts explicitly as local time — see note in Intelligence.jsx.
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

function slugFor(ins, i) {
  return `ins-${ins.number || String(i + 1).padStart(2, '0')}`;
}

export function IntelligenceDetail({ brief, onBack }) {
  const [prefs, setPrefs] = useState(() => briefStore.getPrefs());
  const bundle = bundleMeta(brief?.bundle);
  const rootRef = useRef(null);

  // Keep the rendered brief in state so reordering by scroll works even if
  // the parent re-renders. We read-through to briefStore whenever we persist
  // a prefs change.
  const insights = Array.isArray(brief?.insights) ? brief.insights : [];

  // Sequential edition label for THIS brief within its bundle — counts the
  // bundle's surviving briefs in order, so no gaps from failed generations.
  // We read the store synchronously here rather than wiring a subscription:
  // the detail page is mounted from a list view that just refreshed briefs,
  // so the store is already in sync with what the user saw.
  const displayEdition = useMemo(
    () => displayEditionFor(brief, briefStore.listBriefs()),
    [brief?.id]
  );

  const onJump = (slug) => {
    const root = rootRef.current;
    if (!root) return;
    const el = root.querySelector(`#${slug}`);
    if (el) {
      sfx.nav && sfx.nav();
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const onSpeedChange = (nextSpeed) => {
    const updated = briefStore.updatePrefs({ playbackSpeed: nextSpeed });
    setPrefs(updated);
  };

  const openSource = (url) => {
    if (!url) return;
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {}
  };

  if (!brief) return null;

  return (
    <div ref={rootRef} className="fade-in" style={{ paddingTop: 4 }}>
      {/* Top nav */}
      <div style={{ padding: '8px 18px 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="btn-icon" onClick={onBack} title="Back">
          <Icon name="chevron-l" size={18}/>
        </button>
        <div style={{ flex: 1 }}/>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 10px', borderRadius: 999,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid var(--stroke-2)',
        }}>
          <div style={{ width: 6, height: 6, borderRadius: 999, background: bundle.accent, boxShadow: `0 0 6px ${bundle.accent}` }}/>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--fg-2)', textTransform: 'uppercase' }}>
            {bundle.name}
          </span>
        </div>
      </div>

      {/* Cover */}
      <div style={{ padding: '20px 24px 18px', textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 9, letterSpacing: '0.28em', color: 'var(--gold-2)' }}>
          INTELLIGENCE
        </div>
        <h1 className="brief-serif" style={{
          margin: '8px 0 6px',
          fontSize: 34,
          color: 'var(--fg-1)',
          letterSpacing: '-0.01em',
          lineHeight: 1.05,
        }}>
          {brief.title || 'THE BRIEFING'}
        </h1>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11, letterSpacing: '0.18em', color: 'var(--fg-3)' }}>
          EDITION {displayEdition}
        </div>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
          {humanDate(brief.date || (brief.generatedAt || '').slice(0, 10))}
        </div>

        <div className="gold-hairline" style={{ margin: '16px auto 0', width: '60%' }}/>
      </div>

      {/* Listen CTA / audio player */}
      <div style={{ padding: '0 20px 14px' }}>
        <BriefAudioPlayer
          brief={brief}
          voiceId={prefs.voicePreference}
          initialSpeed={prefs.playbackSpeed}
          onSpeedChange={onSpeedChange}
        />
      </div>

      {/* Table of contents */}
      {insights.length > 0 && (
        <div style={{ padding: '0 20px 12px' }}>
          <div className="eyebrow" style={{ marginBottom: 8, paddingLeft: 4 }}>Contents</div>
          <div style={{ borderRadius: 18, overflow: 'hidden', background: 'rgba(19,21,25,0.55)', border: '1px solid var(--stroke-1)' }}>
            {insights.map((ins, i) => (
              <button
                key={i}
                onClick={() => onJump(slugFor(ins, i))}
                style={{
                  width: '100%',
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '11px 14px',
                  borderBottom: i < insights.length - 1 ? '1px solid var(--stroke-1)' : 'none',
                  background: 'transparent', border: 'none', color: 'var(--fg-2)',
                  textAlign: 'left', cursor: 'pointer',
                  fontFamily: 'var(--font-body)', fontSize: 12.5,
                }}
              >
                <span style={{
                  fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 10,
                  color: 'var(--gold-2)', minWidth: 22,
                }}>
                  {ins.number || String(i + 1).padStart(2, '0')}
                </span>
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ins.title}
                </span>
                <Icon name="chevron-r" size={14} color="var(--fg-4)"/>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      <div style={{ padding: '8px 20px 40px' }}>
        {insights.map((ins, i) => (
          <article
            key={i}
            id={slugFor(ins, i)}
            style={{
              marginTop: i === 0 ? 16 : 28,
              paddingTop: i === 0 ? 0 : 20,
              borderTop: i === 0 ? 'none' : '1px solid var(--stroke-1)',
            }}
          >
            {/* Number + section */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{
                // Newsreader has real weight variants and an opsz axis, so
                // we can dial in 500 italic at display size to get an
                // editorial numeral that feels anchored — the accent-tinted
                // glow still tracks the active theme.
                fontFamily: 'var(--font-serif)', fontWeight: 500, fontStyle: 'italic',
                fontSize: 44,
                color: 'var(--gold-2)', lineHeight: 1, letterSpacing: '-0.02em',
                textShadow: '0 0 22px rgba(var(--accent-rgb), 0.35)',
              }}>
                {ins.number || String(i + 1).padStart(2, '0')}
              </span>
              <span className="brief-section-label">{ins.section || 'INSIGHT'}</span>
            </div>

            {/* Title */}
            <h3 className="brief-serif" style={{
              fontSize: 20, color: 'var(--fg-1)', lineHeight: 1.25, margin: '4px 0 10px',
            }}>
              {ins.title}
            </h3>

            {/* Body */}
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: 13.5, lineHeight: 1.65,
              color: 'var(--fg-2)', margin: 0,
            }}>
              {ins.body}
            </p>

            {/* Strategic Relevance */}
            {ins.strategicRelevance && (
              <div style={{
                marginTop: 14,
                padding: '12px 14px',
                borderRadius: 14,
                background: 'linear-gradient(180deg, rgba(45,156,255,0.07), rgba(45,156,255,0.02))',
                border: '1px solid var(--gold-ring)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
              }}>
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: 9,
                  fontWeight: 800, letterSpacing: '0.18em',
                  color: 'var(--gold-2)', marginBottom: 6,
                  textTransform: 'uppercase',
                }}>
                  Strategic Relevance
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 12.5, lineHeight: 1.55, color: 'var(--fg-2)' }}>
                  {ins.strategicRelevance}
                </div>
              </div>
            )}

            {/* Sources */}
            {Array.isArray(ins.sources) && ins.sources.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {ins.sources.map((s, si) => (
                  <button
                    key={si}
                    onClick={() => openSource(s.url)}
                    className="brief-source"
                  >
                    <Icon name="external" size={11} color="currentColor"/>
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
